/**
 * Dados da tela inicial do Revisor, montados só a partir de /revisor/reports.
 * /revisor/kpi apenas soma os relatórios (totalReports = SPEDs processados,
 * totalCompanies repete o mesmo número), então não é usado aqui.
 *
 * Conformidade = encontradas / (encontradas + faltantes), a mesma conta dos
 * cards antigos. O campo `conformidade.percentual` da API usa outra base
 * (`conformidade.total`, menor que a soma), por isso não é misturado aqui.
 */

export const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_FULL = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export const formatCount = (v) => (v || 0).toLocaleString("pt-BR");
export const formatPercent = (v, digits = 1) => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: digits })}%`;

// "2026-02" → "Fev/26"; com `full`, "Fevereiro de 2026"
export const formatMonthKey = (key, full = false) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  return full ? `${MONTHS_FULL[Number(m) - 1]} de ${y}` : `${MONTHS[Number(m) - 1]}/${y.slice(-2)}`;
};

const keyFromBR = (br) => {
  const m = String(br || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2]}` : "";
};

const monthIndex = (key) => { const [y, m] = key.split("-").map(Number); return y * 12 + (m - 1); };
const keyOfIndex = (i) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;

// "dd/mm/aaaa" do primeiro e do último dia de um mês, no formato do modal
export const monthRangeBR = (key) => {
  const [y, m] = key.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, "0");
  return { start: `01/${mm}/${y}`, end: `${String(last).padStart(2, "0")}/${mm}/${y}` };
};

// "71444475000115" → "71.444.475/0001-15"
export const formatCnpj = (cnpj) => {
  const d = String(cnpj || "").replace(/\D/g, "");
  if (d.length !== 14) return cnpj || "—";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

// Estabelecimento pelo sufixo do CNPJ: 0001 é a matriz
export const branchLabel = (cnpj) => {
  const d = String(cnpj || "").replace(/\D/g, "");
  if (d.length !== 14) return cnpj || "—";
  const branch = d.slice(8, 12);
  return branch === "0001" ? "Matriz" : `Filial ${branch}`;
};

export const conformityColor = (pct) => {
  if (pct >= 90) return "#10b981";
  if (pct >= 70) return "#f59e0b";
  return "#ef4444";
};

export const normalizeReport = (r) => {
  const encontradas = r.encontradas || 0;
  const faltantes = r.faltantes || 0;
  const total = encontradas + faltantes;
  const startKey = keyFromBR(r.period) || (r.periodStart || "").slice(0, 7);
  const endKey = keyFromBR(String(r.period || "").split("-")[1]) || startKey;
  const files = Array.isArray(r.spedFiles) ? r.spedFiles : [];
  return {
    ...r,
    encontradas,
    faltantes,
    total,
    pct: total > 0 ? (encontradas / total) * 100 : 0,
    startKey,
    endKey,
    files,
    nfes: files.reduce((s, f) => s + (f.nfes || 0), 0),
    ctes: files.reduce((s, f) => s + (f.ctes || 0), 0),
    speds: r.spedsProcessed || files.length,
  };
};

export const periodLabel = (r) => {
  if (!r.startKey) return r.period || "Período não definido";
  return r.startKey === r.endKey ? formatMonthKey(r.startKey, true) : `${formatMonthKey(r.startKey)} – ${formatMonthKey(r.endKey)}`;
};

export function buildRevisorHome(rawReports) {
  const reports = rawReports.map(normalizeReport);
  const chrono = [...reports].filter((r) => r.startKey).sort((a, b) => a.startKey.localeCompare(b.startKey));

  const encontradas = reports.reduce((s, r) => s + r.encontradas, 0);
  const faltantes = reports.reduce((s, r) => s + r.faltantes, 0);
  const total = encontradas + faltantes;
  const speds = reports.reduce((s, r) => s + r.speds, 0);

  // Cobertura: cada mês × CNPJ com SPED processado, e o relatório que o cobre
  const cnpjs = new Map();
  const covered = new Map(); // mês → relatório mais recente que o cobre
  reports.forEach((r) => {
    if (!r.startKey) return;
    for (let i = monthIndex(r.startKey); i <= monthIndex(r.endKey); i += 1) {
      const key = keyOfIndex(i);
      const prev = covered.get(key);
      if (!prev || String(r.createdAt) > String(prev.createdAt)) covered.set(key, r);
    }
    r.files.forEach((f) => {
      const cnpj = String(f.cnpj || "").replace(/\D/g, "");
      if (!cnpj) return;
      // Mês do arquivo pelo nome (SPED_<cnpj>_<aaaamm>.txt); sem ele, o início do relatório
      const fromName = String(f.filename || "").match(/_(\d{4})(\d{2})\.\w+$/);
      const key = fromName ? `${fromName[1]}-${fromName[2]}` : r.startKey;
      let c = cnpjs.get(cnpj);
      if (!c) { c = { cnpj, docs: 0, nfes: 0, ctes: 0, months: new Map() }; cnpjs.set(cnpj, c); }
      const docs = (f.nfes || 0) + (f.ctes || 0);
      c.docs += docs;
      c.nfes += f.nfes || 0;
      c.ctes += f.ctes || 0;
      c.months.set(key, { docs, nfes: f.nfes || 0, ctes: f.ctes || 0, report: r });
    });
  });

  const companies = [...cnpjs.values()].sort((a, b) => a.cnpj.localeCompare(b.cnpj));
  const firstKey = chrono[0]?.startKey;
  const lastKey = chrono.reduce((max, r) => (r.endKey > max ? r.endKey : max), firstKey || "");

  // Meses do primeiro ao último analisado; os vazios são as lacunas
  const timeline = [];
  if (firstKey) {
    for (let i = monthIndex(firstKey); i <= monthIndex(lastKey); i += 1) timeline.push(keyOfIndex(i));
  }
  const gaps = timeline.filter((k) => !covered.has(k));

  // Próximo mês a analisar: o seguinte ao último analisado, se já terminou
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const after = lastKey ? keyOfIndex(monthIndex(lastKey) + 1) : "";
  const pendingAfter = [];
  if (after) for (let i = monthIndex(after); i < monthIndex(currentKey); i += 1) pendingAfter.push(keyOfIndex(i));

  const byPct = [...chrono].sort((a, b) => a.pct - b.pct);
  const maxDocs = Math.max(1, ...companies.flatMap((c) => [...c.months.values()].map((m) => m.docs)));

  return {
    reports,
    chrono,
    encontradas,
    faltantes,
    total,
    pct: total > 0 ? (encontradas / total) * 100 : 0,
    speds,
    companies,
    covered,
    timeline,
    gaps,
    pendingAfter,
    suggestion: pendingAfter[0] || gaps[gaps.length - 1] || "",
    worst: byPct[0],
    best: byPct[byPct.length - 1],
    maxDocs,
    firstKey,
    lastKey,
  };
}
